import React, { useMemo } from "react";
import { CardBodySection } from "../../../components";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { Stack, Typography } from "@mui/material";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import { formatDiscountValue } from "@smartrr/shared/utils/displayUtils";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { BigIntString } from "@smartrr/shared/entities/BigIntString";
import { OrderHistorySavedPricingInfo } from "../OrderHistorySavedPricingInfo";
import groupBy from 'lodash/groupBy';
import { DiscountTargetType } from "@smartrr/shared/interfaces/Discount";
import { LoyaltyStoreAccess } from "../../../../Loyalty/store";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";

export const OrderHistoryPricingInfo = ({ order }: { order: ISmartrrOrderFromDb }) => {
  const { discountApplications, orderCurrency } = order;
  const theme = useSmartrrTheme();
  const appliedDiscounts = discountApplications.filter(discount => !discount.rejectionReason && discount.code);
  const isSpecialReferralsEnabled = !!LoyaltyStoreAccess.useSpecialReferralsVariantId();
  const totalShipping = BigIntString.fromBigIntString(order.totalShipping);
  const totalTax = BigIntString.fromBigIntString(order.totalTax);
  const totalAfterDiscounts = BigIntString.fromBigIntString(order.totalNetBeforeRefunds);

  const itemsGroupedBySubscriptionsId = groupBy(
    order.items,
    ({ shopifySubscriptionId }) => shopifySubscriptionId
  );

  const shippingDiscountAmount = appliedDiscounts.find(
    discount => discount.targetType === DiscountTargetType.SHIPPING_LINE && discount.value
  );

  const subtotal = totalAfterDiscounts - totalTax - (shippingDiscountAmount ? 0 : totalShipping);

  const subTotalAmount = formatMoney(subtotal, orderCurrency);

  const isOrderPrepaid = useMemo(() => {
    return order.sts.some(sts => isCPSPrepaid(sts.schedule)) && totalAfterDiscounts === 0;
  }, [order, totalAfterDiscounts]);

  return (
    <CardBodySection borderColor={theme.modernLineDividers}>
      {!!appliedDiscounts.length && !isSpecialReferralsEnabled && (
        <Stack direction="row" justifyContent="space-between" data-testid="order-history-discount-row">
          <Typography variant="body1" whiteSpace="nowrap" data-testid="order-history-discount-title">
            {typedLocalize("general.discount")} (
            <span title={appliedDiscounts.map(discount => discount.code).join(" ")}>
              {appliedDiscounts.map(discount => discount.code).join(" ")}
            </span>
            )
          </Typography>
          <Stack data-testid="order-history-discount-amounts">
            {appliedDiscounts.map((discount, i) => (
              <Typography variant="body1" key={`${discount.code}${i}`}>
                {formatDiscountValue(discount)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        className={prefixClassName("subtotal-line")}
        data-testid="order-history-subtotal-row"
      >
        <Typography variant="body1" whiteSpace="nowrap">
          {typedLocalize("general.subtotal")}
        </Typography>

        {isOrderPrepaid ? (
          <Typography variant="body2">{typedLocalize("general.prepaid")}</Typography>
        ) : (
          <Typography variant="body1">{subTotalAmount}</Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        className={prefixClassName("shipping-line")}
        data-testid="order-history-shipping-row"
      >
        <Typography variant="body1" whiteSpace="nowrap">
          {typedLocalize("orderHistory.shipping")}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: "right" }}>
          {isOrderPrepaid ? (
            <span>{typedLocalize("general.prepaid")}</span>
          ) : (
            <span>{totalShipping === 0 ? "Free" : formatMoney(totalShipping ?? 0, orderCurrency)}</span>
          )}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        className={prefixClassName("total-tax-line")}
        data-testid="order-history-total-tax-row"
      >
        <Typography variant="body1" whiteSpace="nowrap">
          {typedLocalize("orderHistory.totalTax")}
        </Typography>
        {isOrderPrepaid ? (
          <Typography variant="body1">{typedLocalize("general.prepaid")}</Typography>
        ) : (
          <Typography variant="body1">{formatMoney(totalTax ?? 0, orderCurrency)}</Typography>
        )}
      </Stack>
      {!!order.taxLineItems.length && (
        <Stack pl={2} spacing={1.5}>
          {order.taxLineItems.map(taxLineItem => (
            <Stack
              direction="row"
              justifyContent="space-between"
              key={`${taxLineItem.title}|${taxLineItem.rate}`}
            >
              <Typography>
                {taxLineItem.title} ({Math.round(taxLineItem.rate * 1000000) / 10000}%)
              </Typography>
              <Typography>{formatMoney(taxLineItem.price, orderCurrency)}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        className={prefixClassName("total-amount-line")}
        data-testid="order-history-total-amount-row"
      >
        <Typography variant="body2" whiteSpace="nowrap" sx={{ fontWeight: "700 !important" }}>
          {typedLocalize("orderHistory.total")}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: "700 !important" }}>
          {isOrderPrepaid
            ? typedLocalize("general.prepaid")
            : formatMoney(totalAfterDiscounts ?? 0, orderCurrency)}
        </Typography>
      </Stack>
      {Object.keys(itemsGroupedBySubscriptionsId).map(subscriptionId => {
        const subscription = order.sts.find(st => st.shopifyId === subscriptionId);
        return (
          <OrderHistorySavedPricingInfo
            key={subscriptionId}
            subscription={subscription}
            order={order}
            orderItems={itemsGroupedBySubscriptionsId[subscriptionId]}
          />
        );
      })}
    </CardBodySection>
  );
};
